import PresentationStoreModule from '@app/store/modules/PresentationStoreModule';
import IImage from '@app/types/data/base/IImage';
import IMaskedOverviewSlideData from '@app/types/data/slides/IMaskedOverviewSlideData';
import ISlideView from '@app/types/ISlideView';
import ChapterItemType from '@app/types/presentations/ChapterItemType';
import IChapterItem from '@app/types/presentations/IChapterItem';
import ISectionMaster from '@app/types/presentations/master/ISectionMaster';
import ISlideMaster from '@app/types/presentations/master/ISlideMaster';
import { getModule as getStoreModule } from 'vuex-module-decorators';
import INavigationItem from '../../types/data/base/INavigationItem';
import SlideType from '../../types/presentations/SlideType';
import AbstractSlideTransform, { thumbnailCropOptions } from './AbstractSlideTransform';

/**
 * Transform for 007-MaskedOverviewSlide
 */
export default class MaskedOverviewSlideTransform extends AbstractSlideTransform {
	public viewComponent: string = 'MaskedOverviewSlide';

	public async transform(slide: ISlideMaster): Promise<IMaskedOverviewSlideData> {
		const presentationStore: PresentationStoreModule = getStoreModule(PresentationStoreModule);

		const image = this.transformImageAsset(slide.fields.image?.fields.asset ?? this.getFallbackImage(), {
			width: 5000,
			height: 5000,
			crop: 'limit'
		});

		let logo: IImage | undefined;

		if (slide.fields.logo && slide.fields.logo.fields.asset) {
			logo = {
				...this.transformImageAsset(slide.fields.logo.fields.asset, {
					width: 390,
					height: 250,
					crop: 'limit'
				}),
				alt: slide.fields.logo.fields.title
			};
		}

		const data: IMaskedOverviewSlideData = {
			id: slide.id,
			label: slide.fields.label,
			thumbnail: this.extractThumbnail(slide.fields.headline, slide, 'image'),
			image,
			logo,
			svgMask: slide.fields.svgMask,
			navigationItems: []
		};

		const chapterId: string = Object.keys(presentationStore.slideIdsByChapterId).find(
			// eslint-disable-next-line @typescript-eslint/no-shadow
			(chapterId: string): boolean => presentationStore.slideIdsByChapterId[chapterId].includes(slide.id)
		) as string;

		const chapterItems: IChapterItem[] = presentationStore.selectedChapterItemsByChapterId[chapterId];

		data.navigationItems = (
			await Promise.all(
				chapterItems.map(async (item: IChapterItem): Promise<INavigationItem | undefined> => {
					if (item.type === ChapterItemType.Section) {
						// item is a section - use label and thumbnail from sectionMaster, but link to first section slide
						const masterSection: ISectionMaster = presentationStore.master.sectionsById[item.id];

						const firstSelectedSectionSlideId: string = presentationStore.selectedSlideIds.find(
							(slideId: string): boolean => masterSection.slides.includes(slideId)
						) as string;

						return {
							id: firstSelectedSectionSlideId,
							svgId: masterSection.svgId,
							label: masterSection.title,
							image: this.transformImageAsset(masterSection.thumbnail, thumbnailCropOptions)
						};
					}
					// item is a slide

					const slideMaster: ISlideMaster = presentationStore.master.slidesById[item.id];

					// don't include self, custom slides or chapter overviews
					if (!slideMaster || item.id === slide.id || slideMaster.type === SlideType.ChapterOverviewSlide) {
						return undefined;
					}

					const slideView: ISlideView = await presentationStore.slideViewPromisesById[item.id];

					return {
						id: item.id,
						svgId: slideMaster.fields.svgId,
						label: slideView.data.label,
						image: slideView.data.thumbnail
					};
				})
			)
		).filter((navigationItem: INavigationItem | undefined): navigationItem is INavigationItem => !!navigationItem);

		return data;
	}
}
